import type {
  StripeError,
  StripeErrorType,
  PaymentIntent,
  PaymentMethod,
  SetupIntent,
  Source,
} from '@stripe/stripe-js';

export class PaymentMethodError {
  /**
   * The type of error.
   */
  type: StripeErrorType;

  /**
   * For card errors, the ID of the failed charge
   */
  charge?: string;

  /**
   * For some errors that could be handled programmatically, a short string indicating the [error code](https://stripe.com/docs/error-codes) reported.
   */
  code?: string;

  /* eslint-disable @typescript-eslint/naming-convention */
  /**
   * For card errors resulting from a card issuer decline, a short string indicating the [card issuer’s reason for the decline](https://stripe.com/docs/declines#issuer-declines) if they provide one.
   */
  decline_code?: string;

  /**
   * A URL to more information about the [error code](https://stripe.com/docs/error-codes) reported.
   */
  doc_url?: string;

  /**
   * A human-readable message providing more details about the error. For card errors, these messages can be shown to your users.
   */
  message?: string;

  /**
   * If the error is parameter-specific, the parameter related to the error.
   * For example, you can use this to display a message near the correct form field.
   */
  param?: string;

  /**
   * The `PaymentIntent` object for errors returned on a request involving a `PaymentIntent`.
   */
  payment_intent?: PaymentIntent;

  /**
   * The `PaymentMethod` object for errors returned on a request involving a `PaymentMethod`.
   */
  payment_method?: PaymentMethod;

  /**
   * The `SetupIntent` object for errors returned on a request involving a `SetupIntent`.
   */
  setup_intent?: SetupIntent;
  /* eslint-enable @typescript-eslint/naming-convention */

  /**
   * The `Source` object for errors returned on a request involving a `Source`.
   */
  source?: Source;

  constructor(
    error:
      | StripeError
      | PaymentIntent.LastPaymentError
      | SetupIntent.LastSetupError,
  ) {
    this.type = error.type;
    this.charge = error.charge;
    this.code = error.code;
    this.decline_code = error.decline_code;
    this.doc_url = error.doc_url;
    this.message = error.message;
    this.param = error.param;
    this.payment_method = error.payment_method;

    if ('payment_intent' in error) {
      this.payment_intent = error.payment_intent;
    }

    if ('setup_intent' in error) {
      this.setup_intent = error.setup_intent;
    }
  }
}
